import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, Subject, lastValueFrom, of, firstValueFrom } from 'rxjs';
import { Team, TeamToggleValue } from 'src/app/shared/models/team/team';
import { TeamClone } from 'src/app/shared/models/team/teamClone';
import { TeamWrite } from 'src/app/shared/models/team/teamWrite';
import { TeamDescriptionWrite } from 'src/app/shared/models/team/teamDescriptionWrite';
import { Position } from 'src/app/shared/models/team/position';
import { PositionWrite } from 'src/app/shared/models/team/positionWrite';
import { catchError, first, map, publishLast, refCount } from 'rxjs/operators';
import { AttributeDefinition } from 'src/app/shared/models/attributes/attributeDefinition';
import { TeamFilterService } from '../../teams/teams-home/team-filter.service';
import { SeatStatus } from 'src/app/shared/models/team/seatStatus';
import { Seat } from 'src/app/shared/models/team/seat';
import { Attribute } from 'src/app/shared/models/attributes/attribute';
import { Person } from 'src/app/people/models/person';
import { BaseService } from './base.service';
import { genericErrorMessage, getParsedError } from './error-handler-factory.service';
import { AttributeWrite } from '../models/attributes/attributeWrite';
import { ApiVersions } from './api-version';
import { PersonDetailsService } from 'src/app/shared/services/person-details.service';
import { InjectorModule } from '../modules/injector.module';
import { WorkspaceService } from './workspace.service';
import { HttpErrorResponse } from '@angular/common/http';
import { PeopleCurrentUpcomingTeamsSummary } from '../models/team/peopleCurrentUpcomingTeamsSummary';
import { PositionListing } from 'src/app/shared/models/positionListing';
import { PositionListingWrite } from '../models/positionListingWrite';

@Injectable()
export class TeamService extends BaseService {
  basePath: string = 'teams';
  teamToggleValue: TeamToggleValue = TeamToggleValue.Roster;

  public positionListingUpdated: Subject<PositionListing> = new Subject<PositionListing>();
  private _team: BehaviorSubject<Team> = new BehaviorSubject<Team>(null);
  private _teams: BehaviorSubject<Team[]> = new BehaviorSubject(null);
  public readonly peopleCurrentUpcomingSummary: BehaviorSubject<
    Record<string, PeopleCurrentUpcomingTeamsSummary>
  > = new BehaviorSubject(null);
  private _selectedPosition: Position = null;
  private personDetailsService: PersonDetailsService;
  public currentSeatId: string;

  public readonly team: Observable<Team> = this._team.asObservable();
  public readonly teams: Observable<Team[]> = this._teams.asObservable();
  public readonly validationErrors: Subject<string[]> = new Subject<string[]>();
  //** published when a person picked for position may need their record on the person page refreshed */
  public personPicked: Subject<void> = new Subject<void>();
  public peopleOnTeam = new BehaviorSubject<{ people: Person[]; rosterType: string }>(null);

  public get currentTeam(): Team {
    return this._team?.getValue();
  }

  constructor(
    private teamFilterService: TeamFilterService,
    private workspaceService: WorkspaceService
  ) {
    super();

    // used InjectorModule because of the circular dependency issue that showed up in the unit tests.
    this.personDetailsService = InjectorModule.injector.get(PersonDetailsService);

    this.personDetailsService.personUpdated.subscribe((person) => {
      if (!person) return;
      this.updateTeamPerson(person);
    });
    this.personDetailsService.personDeleted.subscribe((person) => {
      if (!person) return;
      this.deleteTeamPerson(person);
    });
  }

  refreshTeam() {
    if (this._team && this._team.value) {
      this.getTeamById(this._team.value.id);
    }
  }
  getTeamById(id: string): Observable<Team> {
    let path = this.basePath.concat('/', id);
    let obs = this.get(path, null, ApiVersions.v1_0).pipe(map((data) => Team.parse(data)));

    obs.subscribe((result) => {
      if (!result) return;
      this._team.next(result);

      let teams = this._teams.getValue();
      if (!teams) return;
      let index = teams.findIndex((t) => t.id == result.id);
      teams[index] = result;

      this._teams.next(teams);
    });

    return obs;
  }

  resyncHLTeam(id: string): void {
    let path = `higher-logic/${id}/resync`;
    this.get(path, null, ApiVersions.unversioned).subscribe((result) => {
      this.openSnackBar('resync to higher logic successfully', true);
    });
  }

  getTeamsUsingSameHLCommunity(id: string): Observable<any> {
    let path = `higher-logic/${id}/teams-using-same-community`;
    return this.get(path, null, ApiVersions.unversioned);
  }

  getTeamsUsingExistingCommunities(id: string): Observable<any> {
    let path = `higher-logic/${id}/existing-communities`;
    return this.get(path, null, ApiVersions.unversioned);
  }

  linkHLTeamToNewCommunity(teamId: string, communityId: string): Observable<any> {
    let path = `higher-logic/${teamId}/${communityId}/link-new-community`;
    let obs = this.put(path, null, null, ApiVersions.unversioned);
    obs.subscribe((updatedTeam) => {
      if (updatedTeam) {
        this.updateTeamOnTeams(updatedTeam, teamId);
        this.openSnackBar('Team successfully linked to new community', true);
      }
    });
    return obs;
  }

  removeCommunityLinkHLTeam(id: string): Observable<any> {
    let path = `higher-logic/${id}/remove-link-community`;
    let obs = this.delete(path, null, ApiVersions.unversioned, true).pipe(
      catchError((err: HttpErrorResponse, caught) => {
        if (err.status == 404) {
          this.openSnackBar('team or community not found');
          return;
        } else {
          return this.handleError(err, true, false);
        }
      })
    );
    obs.subscribe((updatedTeam) => {
      if (updatedTeam) {
        this.updateTeamOnTeams(updatedTeam, id);
        this.openSnackBar('Removing link to higher logic community successfully', true);
      }
    });
    return obs;
  }

  createNewCommunity(teamId: string): Observable<any> {
    let path = `higher-logic/${teamId}/create-higher-logic-community`;
    let obs = this.put(path, null, null, ApiVersions.unversioned);
    obs.subscribe((updatedTeam) => {
      if (updatedTeam) {
        this.updateTeamOnTeams(updatedTeam, teamId);
        this.openSnackBar('Team successfully linked to new community', true);
      }
    });
    return obs;
  }

  getAllTeamSeats(teamId: string) {
    let path = `${this.basePath}/${teamId}/all-seats`;
    return this.get(path, null, ApiVersions.v0_1);
  }

  async getTeams(): Promise<Team[]> {
    let path = `${this.basePath}/summaries`;
    let obs = this.get(path).pipe(
      map((data) => (data ?? []).map((team) => Team.parseFromSummary(team)))
    );

    const teams = await firstValueFrom(obs);
    this._teams.next(teams);
    return teams;
  }

  getPeopleOnTeam(teamId: string) {
    let date: string = '';
    let path = `${this.basePath}/${teamId}/people/${date}`;
    let obs = this.get(path, null, ApiVersions.v1_0).subscribe((result) => {
      if (result) {
        let people: Person[] = result.peopleOnTeam.map((person) => Person.parseForSearch(person));
        this.peopleOnTeam.next({ people: people, rosterType: result.rosterType });
      }
    });
  }

  deleteTrackedFieldValue(teamId: string, seat: Seat, attributeDefinitionId: string) {
    let path = `${this.basePath}/${teamId}/seat/${seat.id}/tracked-fields/${attributeDefinitionId}`;
    let obs = this.delete(path, null, ApiVersions.v1_0).pipe(publishLast()).pipe(refCount());

    obs.subscribe((data) => this.refreshSeatAttribute(seat, attributeDefinitionId, []));

    return obs;
  }

  deleteTrackedField = async (teamId: string, attributeDefinitionId: string): Promise<boolean> => {
    try {
      const result = await lastValueFrom(
        this.delete(
          `${this.basePath}/${teamId}/tracked-fields/${attributeDefinitionId}`,
          null,
          ApiVersions.v1_0,
          true
        )
      );
      if (!result) {
        this.openSnackBar('Failed to delete tracked field', false);
        return false;
      }
    } catch (error) {
      const message = getParsedError(error).message;
      this.openSnackBar(message, false);
      return false;
    }
    return true;
  };

  updateTrackedFieldValue = async (
    teamId: string,
    seat: Seat,
    attributeDefinitionId: string,
    values: AttributeWrite
  ): Promise<boolean> => {
    let path = `${this.basePath}/${teamId}/seat/${seat.id}/tracked-fields/${attributeDefinitionId}`;
    const attributes: Attribute[] = await lastValueFrom(
      this.post(path, JSON.stringify(values), null, ApiVersions.v1_0)
        .pipe(map((attributes) => attributes?.map((value) => Attribute.parse(value))))
        .pipe(publishLast())
        .pipe(refCount())
    );

    if (attributes?.length) {
      let updatedData = attributes.filter(
        (attr) => attr.attributeDefinitionId == attributeDefinitionId
      );
      this.refreshSeatAttribute(seat, attributeDefinitionId, updatedData);
      return true;
    }

    return false;
  };

  setSeatId(seatId: string) {
    this.currentSeatId = seatId;
  }

  getSeatId() {
    return this.currentSeatId;
  }

  clearTeam() {
    this._team.next(null);
  }

  hasTeam() {
    return this._team.getValue();
  }

  hasTeamById(teamId): Observable<boolean> {
    return new Observable((observer) => {
      this.teams.subscribe((teams) => {
        observer.next(teams.findIndex((t) => t.id == teamId) != -1);
        observer.complete();
      });
    });
  }

  updateTeamPerson(person) {
    let teams = this._teams.getValue();
    let team = this._team.getValue();
    if (!teams || !team) return;
    let index = teams.findIndex((t) => t.id == team.id);

    team.updatePerson(person);
    teams[index] = team;

    this._teams.next(teams);
    this._team.next(team);
  }

  private refreshSeatAttribute(
    seat: Seat,
    attributeDefinitionId: string,
    updatedAttributes: Attribute[]
  ) {
    let svc = this;
    let teams = this._teams.getValue();
    let team = this._team.getValue();
    let index = teams.findIndex((t) => t.id == team.id);

    team.updateSeatAttribute(seat, attributeDefinitionId, updatedAttributes);
    teams[index] = team;

    svc._teams.next(teams);
    svc._team.next(team);
  }

  deleteTeamPerson(person) {
    let svc = this;
    let teams = this._teams.getValue();
    let team = this._team.getValue();
    if (!team) return;
    let index = teams.findIndex((t) => t.id == team.id);

    team.deletePerson(person);
    teams[index] = team;

    svc._teams.next(teams);
    svc._team.next(team);
  }

  updateTeamEmailCustomization(emailCustomization) {
    let svc = this;
    let teams = this._teams.getValue();
    let team = this._team.getValue();
    let index = teams.findIndex((t) => t.id == team.id);

    team.updateEmailCustomization(emailCustomization);
    teams[index] = team;

    svc._teams.next(teams);
    svc._team.next(team);
  }

  createTeam(teamWrite: TeamWrite) {
    let body = JSON.stringify(teamWrite);
    let obs = this.post(this.basePath, body).pipe(map((data) => Team.parse(data)));

    obs.subscribe((newTeam) => {
      if (newTeam) {
        let teams = this._teams.getValue();
        teams.push(newTeam);

        this._teams.next(teams);
        this._team.next(newTeam);
      }
    });
  }

  cloneTeam(teamId: string, teamClone: TeamClone): Observable<Team> {
    let body = JSON.stringify(teamClone);
    let path = this.basePath.concat('/', teamId, '/clone');
    let obs = this.post(path, body).pipe(map((data) => Team.parse(data)));

    obs.subscribe((newTeam) => {
      let teams = this._teams.getValue();
      teams.push(newTeam);

      this._teams.next(teams);
      this._team.next(newTeam);
    });

    return obs;
  }

  updateTeam(team: TeamWrite) {
    let body = JSON.stringify(team);
    let path = this.basePath.concat('/', team.id);

    return this.sendTeamUpdate(path, team.id, body);
  }

  updateTeamDescription(team: TeamDescriptionWrite, callback: any) {
    let path = this.basePath.concat(`/${team.id}/description`);

    return this.sendTeamUpdate(path, team.id, team, null, false, callback);
  }

  updateTeamCustomField = async (
    teamId: string,
    attributeDefinitionId: string,
    attribute: AttributeWrite
  ): Promise<boolean> => {
    const attributes: Attribute[] = await lastValueFrom(
      this.post(
        this.basePath.concat(`/${teamId}/custom-fields/${attributeDefinitionId}`),
        JSON.stringify(attribute)
      )
    );
    if (attributes) {
      this.reloadTeamAttributes(attributes);
      return true;
    }
    return false;
  };

  deleteTeamCustomField(teamId: string, attributeDefinitionId: string): Observable<any> {
    let path = this.basePath.concat(`/${teamId}/custom-fields/${attributeDefinitionId}`);
    let obs = this.delete(path);

    obs.subscribe((attributes) => {
      this.reloadTeamAttributes(attributes);
    });
    return obs;
  }

  private reloadTeamAttributes(attributes: Attribute[]): void {
    if (attributes) {
      let team = this._team.getValue();
      team.attributes = (attributes as []).map((a) => Attribute.parse(a));
      this._team.next(team); // update team info

      let teams = this._teams.getValue();
      if (!teams) return;
      let index = teams.findIndex((t) => t.id === team.id);
      teams[index] = team;
      this._teams.next(teams); // update list of teams info
    }
  }

  updateTrackedFields(teamId: string, seatAttributes: AttributeDefinition[]): Observable<Team> {
    let path = this.basePath.concat(`/${teamId}/tracked-fields`);
    let body = JSON.stringify(seatAttributes);
    let obs = this.post(path, body, null, ApiVersions.v1_0);
    return obs;
  }

  async createPosition(position: PositionWrite) {
    const teamId = this._team.getValue()?.id;
    let path = `${this.basePath}/${teamId}/positions`;
    let body = JSON.stringify(position);

    const newPosition = await lastValueFrom(
      this.post(path, body).pipe(map((data) => Position.parse(data)))
    );

    this.updateTeamWithPosition(newPosition);
  }

  async getSeatEligibility(teamId: string, seatId: string): Promise<boolean> {
    const path = `${this.basePath}/${teamId}/positions/seats/${seatId}/eligibility`;
    return lastValueFrom(this.get<boolean>(path));
  }

  async updatePosition(position: PositionWrite) {
    const teamId = this._team.getValue()?.id;
    let path = `${this.basePath}/${teamId}/positions/${position.id}`;
    let body = JSON.stringify(position);

    const updatedPosition = await lastValueFrom(
      this.put(path, body).pipe(map((data) => Position.parse(data)))
    );
    this.updateTeamWithPosition(updatedPosition);
  }

  deletePosition(teamId: string, positionId: string): Observable<any> {
    let path = `${this.basePath}/${teamId}/positions/${positionId}`;

    return this.delete(path).pipe(
      map((removedPosition) => {
        if (removedPosition) {
          this.removePositionOnTeam(teamId, removedPosition);
        }
      })
    );
  }

  overrideSeatStatus(teamId: string, seat: Seat, status: SeatStatus) {
    let path = `${this.basePath}/${teamId}/positions/seats/${seat.id}/status`;
    let body = JSON.stringify(SeatStatus[status]);
    const sub = this.sendTeamUpdate(path, teamId, body, null, false, null, ApiVersions.v1_0);
    sub.add(() => {
      this.personDetailsService.resetTeamHistory();
    });
    return sub;
  }

  updateSeat(
    teamId: string,
    positionId: string,
    seatId: string,
    personId: string,
    status: SeatStatus,
    startDate: string,
    endDate: string
  ): Observable<any> {
    let body = {
      positionId: positionId,
      personId: personId,
      status: SeatStatus[status],
      startDate: startDate,
      endDate: endDate
    };
    let path;
    if (seatId) {
      path = `${this.basePath}/${teamId}/positions/seats/${seatId}`;
      return this.patch(path, body, ApiVersions.v1_0, true).pipe(
        catchError((err) => this.handleErrors(err))
      );
    } else {
      let seatModel = JSON.stringify(body);
      path = `${this.basePath}/${teamId}/positions/add-seat`;
      return this.put(path, seatModel, null, ApiVersions.v1_0, true).pipe(
        catchError((err) => this.handleErrors(err))
      );
    }
  }

  private handleErrors(errorResponse: HttpErrorResponse) {
    if (errorResponse.error && errorResponse.status === 400) {
      this.validationErrors.next(errorResponse.error);
      return of(null);
    } else {
      this.handleError(errorResponse);
      return of(null);
    }
  }

  unpickPerson(teamId: string, seatId: string) {
    let path = `${this.basePath}/${teamId}/positions/seats/${seatId}`;

    return this.delete(path, null, ApiVersions.v1_0).subscribe((result) => {
      if (result) {
        this.router.navigate(['teams', teamId]);
        this.getTeamById(teamId);
        this.openSnackBar('Seat removed successfully', true);
      }
      if (this.personDetailsService.currentPerson) {
        this.workspaceService.getPersonWorkspaces(this.personDetailsService.currentPerson.id);
        if (this.personDetailsService.currentPerson.activeTeamsCountInCurrentWorkspace > 0) {
          this.personDetailsService.currentPerson.activeTeamsCountInCurrentWorkspace--;
        }
      }
    });
  }

  promotePosition(teamId: string, positionId: string) {
    let path = `${this.basePath}/${teamId}/positions/${positionId}/promote`;

    return this.sendTeamUpdate(path, teamId);
  }

  demotePosition(teamId: string, positionId: string) {
    let path = `${this.basePath}/${teamId}/positions/${positionId}/demote`;

    return this.sendTeamUpdate(path, teamId);
  }

  setAlltoAccepted(teamId: string, seatIds: string[]) {
    let path = `${this.basePath}/${teamId}/positions/bulk-accepted`;
    return this.sendUpdate(path, teamId, seatIds, ' seats changed to accepted');
  }

  setPickedtoInvited(teamId: string, seatIds: string[]) {
    let path = `${this.basePath}/${teamId}/positions/bulk-invited`;
    return this.sendUpdate(path, teamId, seatIds, ' seats changed to invited');
  }

  sendUpdate(path: string, teamId: string, body: any = null, successMessage) {
    return this.put(path, body, null, ApiVersions.v1_0, true)
      .pipe(catchError((err) => this.showErrors(err)))
      .subscribe((result) => {
        if (result) {
          this.updateTeamOnTeams(result, teamId);
          this.openSnackBar(result.seatsChanged.length + successMessage, true);
        }
      });
  }

  private showErrors(errorResponse: HttpErrorResponse) {
    if (errorResponse.error && (errorResponse.status === 400 || errorResponse.status === 404)) {
      this.openSnackBar(errorResponse.error, false);
      return of(null);
    } else {
      this.handleError(errorResponse);
      return of(null);
    }
  }

  patchTeamMetadata(teamId: string, externalSystemId: string, successMessage: string) {
    let path = `${this.basePath}/${teamId}/metadata`;
    this.patch(path, { externalSystemId: externalSystemId }, ApiVersions.v0_1).subscribe(
      (result) => {
        if (result) {
          this.updateTeamOnTeams(result, teamId);
          this.openSnackBar(successMessage, true);
        }
      }
    );
  }

  /** returns null and shows snack error message on any error */
  async bulkPick(
    teamId: string,
    positionId: string,
    startDate: string,
    endDate: string,
    activePeopleIds: string[],
    status: string
  ): Promise<Team> {
    let path = `${this.basePath}/${teamId}/positions/bulk-pick`;
    let body: any = {
      activePeopleIds: activePeopleIds,
      positionId: positionId,
      startDate: startDate,
      endDate: endDate,
      status: status
    };
    let updatedTeam: Team;
    try {
      updatedTeam = (await lastValueFrom(
        this.put(path, body, null, ApiVersions.v1_0, true)
      )) as Team;
    } catch (four00Error) {
      const message = getParsedError(four00Error).message;
      this.openSnackBar(message, false);
      return null;
    }

    if (!updatedTeam?.bulkPickPeopleCount) {
      console.error('expected updatedTeam.bulkPickPeopleCount to be set');
      this.openSnackBar(genericErrorMessage, false);
      return null;
    }
    if (activePeopleIds.length === 1) {
      //picked a single person. Send out some updates
      this.personPicked.next();
      this.personDetailsService.resetTeamHistory();
    }

    this.updateTeamOnTeams(updatedTeam, teamId);
    if (
      this.personDetailsService.currentPerson &&
      activePeopleIds.find((id) => id == this.personDetailsService.currentPerson.id)
    ) {
      this.personDetailsService.currentPerson.activeTeamsCountInCurrentWorkspace++;
    }
    return updatedTeam;
  }

  bulkAddPeopleOnBenchToPosition = (
    teamId: string,
    positionId: string,
    startDate: string,
    endDate: string,
    activePeopleIds: string[],
    status: SeatStatus
  ) => {
    let path = `${this.basePath}/${teamId}/positions/bulk-add-bench-to-position`;
    let body: any = {
      activePeopleIds: activePeopleIds,
      positionId: positionId,
      startDate: startDate,
      endDate: endDate,
      status: SeatStatus[status]
    };
    return this.put(path, body, null, ApiVersions.v1_0);
  };

  updateSeatExternalSystemId(
    teamId: string,
    seatId: string,
    externalSystemId: string,
    successMessage: string
  ) {
    let path = `${this.basePath}/${teamId}/positions/seats/${seatId}/metadata`;
    return this.patch(path, { externalSystemId: externalSystemId }, ApiVersions.v0_1).subscribe(
      (result) => {
        if (result) {
          this.updateTeamOnTeams(result, teamId);
          this.openSnackBar(successMessage, true);
        }
      }
    );
  }

  bulkInvite(
    teamId: string,
    seatIds: Array<string>,
    emailSubject: string,
    emailContent: string,
    successMessage: string,
    reminderDaysFromNow?: number,
    contactName?: string,
    contactEmail?: string
  ) {
    let path = `${this.basePath}/${teamId}/bulk-invite`;
    let body = {
      allPicked: false,
      memberSeats: seatIds,
      email: {
        subject: emailSubject,
        content: emailContent,
        contactName: contactName,
        contactEmail: contactEmail
      },
      reminderDaysFromNow: reminderDaysFromNow
    };

    return this.sendTeamUpdate(path, teamId, body, successMessage, true, null, ApiVersions.v1_0);
  }

  bulkRemind(
    teamId: string,
    emailSubject: string,
    emailContent: string,
    successMessage?: string,
    contactName?: string,
    contactEmail?: string
  ) {
    let path = `${this.basePath}/${teamId}/bulk-invite-reminder`;
    const email = {
      subject: emailSubject,
      content: emailContent,
      contactName: contactName,
      contactEmail: contactEmail
    };

    return this.sendTeamUpdate(path, teamId, email, successMessage, true, null, ApiVersions.v1_0);
  }

  archive(teamId: string) {
    let path = `${this.basePath}/${teamId}/archive`;

    var obs = this.patch(path);

    obs.subscribe((updatedTeam) => {
      this.updateTeamOnTeams(updatedTeam, teamId);
      this.openSnackBar(`Successfully archived ${updatedTeam.name}.`, true);
    });

    return obs;
  }

  unarchive(teamId: string) {
    let path = `${this.basePath}/${teamId}/unarchive`;

    var obs = this.patch(path);

    obs.subscribe((updatedTeam) => {
      this.updateTeamOnTeams(updatedTeam, teamId);
      this.openSnackBar(`Successfully unarchived ${updatedTeam.name}.`, true);
    });

    return obs;
  }

  migrateToWorkspace(teamId: string, workspaceId: string) {
    let path = `${this.basePath}/${teamId}/migrate/${workspaceId}`;

    var obs = this.post(path, null);
    obs.subscribe((updatedTeam) => {
      this.openSnackBar(`Successfully migrated ${updatedTeam.name} to workspace.`, true);
    });

    return obs;
  }

  deleteTeam(teamId: string): Observable<any> {
    let path = `${this.basePath}/${teamId}`;

    return this.delete(path).pipe(
      map((response) => {
        if (response) {
          let teams = this._teams.getValue();
          let index = teams.findIndex((t) => t.id == teamId);

          if (index > -1) {
            teams.splice(index, 1);
            this._teams.next(teams);
          }

          let team = this._team.getValue();
          if (team && team.id == teamId) {
            this.clearTeam();
          }

          this.teamFilterService.removeDeletedTeamFromFilteredResult(teamId);
        }
      })
    );
  }

  getSelectedPosition() {
    return this._selectedPosition;
  }

  resetSelectedPosition() {
    this.setSelectedPosition(null);
  }

  setSelectedPosition(position: Position) {
    this._selectedPosition = position;
  }

  sendEmail(
    teamId: string,
    seats: string[],
    fromName: string,
    replyTo: string,
    subject: string,
    content: string
  ) {
    let path = `${this.basePath}/${teamId}/groupEmail`;
    let obs = this.post(path, {
      all: false,
      seats: seats,
      fromName: fromName,
      replyTo: replyTo,
      subject: subject,
      content: content
    });

    obs.subscribe((ok) => {
      this.openSnackBar(`Email sent to ${seats.length} seats.`, true);
    });

    return obs;
  }

  getTeamPositions(teamId) {
    let path = `${this.basePath}/${teamId}/positions`;
    return this.get<Position[]>(path, true);
  }

  private updateTeamWithPosition(position: Position) {
    let teams = this._teams.getValue();
    let index = teams.findIndex((t) => t.id == position.teamId);
    let team = teams[index];

    team.addPosition(position);

    team.updatedOn = position.updatedOn;
    teams[index] = team;
    this._teams.next(teams);
    this._team.next(team);
  }

  private removePositionOnTeam(teamId: string, position: Position) {
    let teams = this._teams.getValue();
    let teamIndex = teams.findIndex((t) => t.id == teamId);
    let team = teams[teamIndex];

    var success = team.removePosition(position);
    if (success) {
      teams.indexOf[teamIndex] = team;

      this._team.next(team);
      this._teams.next(teams);
    }
  }

  private sendTeamUpdate(
    path: string,
    teamId: string,
    body: any = null,
    successMessage: string = null,
    isPut: boolean = true,
    callback: any = null,
    apiVersion: ApiVersions = ApiVersions.v0_1
  ) {
    let service = this;
    if (isPut) {
      return service.put(path, body, null, apiVersion).subscribe((updatedTeam) => {
        if (updatedTeam) {
          service.updateTeamOnTeams(updatedTeam, teamId);
          if (successMessage) {
            service.openSnackBar(successMessage, true);
          }
          if (callback) {
            callback();
          }
        }
      });
    } else {
      return service.patch(path, body, apiVersion).subscribe((updatedTeam) => {
        if (updatedTeam) {
          service.updateTeamOnTeams(updatedTeam, teamId);
          if (successMessage) {
            service.openSnackBar(successMessage, true);
          }
          if (callback) {
            callback();
          }
        }
      });
    }
  }

  updateTeamOnTeams(updatedTeam: Team, teamId: string) {
    const team = Team.parse(updatedTeam);

    const teams = this._teams.getValue();
    const index = teams.findIndex((t) => t.id == teamId);
    teams[index] = team;

    this._teams.next(teams);

    this._team.next(team);

    this.teamFilterService.updateTeamFromFilteredResult(updatedTeam, teamId);
  }

  getPeopleCurrentUpcomingTeamsSummary(peopleIds: string[]) {
    this.post(
      'people/current-upcoming-teams-summary',
      JSON.stringify(peopleIds),
      null,
      ApiVersions.unversioned
    )
      .pipe(first())
      .subscribe((data) => {
        if (data) {
          this.peopleCurrentUpcomingSummary.next(data);
        }
      });
  }

  getListingbyId(
    teamId: string,
    positionId: string,
    listingId: string
  ): Observable<PositionListing> {
    let path = `teams/${teamId}/positions/${positionId}/opportunity-listing/${listingId}`;
    return this.get(path).pipe(map((data) => PositionListing.parse(data)));
  }

  updatePositioninTeam(teamId: string, position: any) {
    let teams = this._teams.getValue();
    let teamIndex = teams.findIndex((t) => t.id == teamId);
    let team = teams[teamIndex];
    var success = team.updatePosition(Position.parse(position));
    if (success) {
      teams.indexOf[teamIndex] = team;

      this._team.next(team);
      this._teams.next(teams);
    }
  }

  createListing(teamId: string, listing: PositionListingWrite): Observable<any> {
    let path = `teams/${teamId}/positions/opportunity-listing`;
    let body = JSON.stringify(listing);
    let obs = this.post(path, body);
    obs.subscribe((res) => {
      if (res) {
        this.updatePositioninTeam(teamId, res);
        this.openSnackBar('Position listing was created successfully', true);
      }
    });
    return obs;
  }

  updateListing(
    teamId: string,
    listingId: string,
    listing: PositionListingWrite,
    notUsingRouter: boolean
  ): Observable<any> {
    let path = `teams/${teamId}/positions/opportunity-listing/${listingId}`;
    let body = JSON.stringify(listing);
    let obs = this.put(path, body);
    obs.subscribe((res) => {
      if (res) {
        if (notUsingRouter && res.positionListings?.length > 0) {
          this.positionListingUpdated.next(res.positionListings[0]);
        }
        this.updatePositioninTeam(teamId, res);
        this.openSnackBar('Position listing was updated successfully', true);
      }
    });
    return obs;
  }

  deleteListing(teamId: string, positionId: string, listingId: string): Observable<any> {
    let path = `teams/${teamId}/positions/${positionId}/opportunity-listing/${listingId}`;
    let obs = this.delete(path);
    obs.subscribe((res) => {
      if (res) {
        this.updatePositioninTeam(teamId, res);
        this.openSnackBar('Position listing deleted successfully', true);
      }
    });
    return obs;
  }

  getTeamCredentialAbout = (teamId: string): Observable<any> => {
    let path = `teams/${teamId}/team-credential-about`;
    return this.get(path, null, ApiVersions.v1_0);
  };
}
